import { getJson } from '$lib/helpers/util.js'

const state = $state({
	googleSheetKey: null,
	googleSheetSheetName: 'sheet1',
	mapdata: [],
	legend: [],
	headline: "",
	subtitle: "",
	source: "",
	popupTemplate: "Use single mustache tags for each column in the row",
})

// Common column names for lat/lng (case-insensitive match)
const LAT_NAMES = [
	'latitude',
	'lat',
	'latitud',
	'y' // sometimes used for lat in CSV exports
]
const LNG_NAMES = [
	'longitude',
	'long',
	'lng',
	'lon',
	'lngitude',
	'x' // sometimes used for lng in CSV exports
]

function findColumn(names, keys) {
	const lower = (s) => String(s ?? '').trim().toLowerCase()
	const keyList = keys.map(lower)
	for (const name of names) {
		const i = keyList.indexOf(name)
		if (i !== -1) return keys[i]
	}
	return null
}

function parseCoord(value) {
	if (value === null || value === undefined) return NaN
	const n = parseFloat(String(value).trim())
	return Number.isFinite(n) ? n : NaN
}

function hasValidCoords(row, latKey, lngKey) {
	const lat = parseCoord(row[latKey])
	const lng = parseCoord(row[lngKey])
	return !Number.isNaN(lat) && !Number.isNaN(lng)
}

/**
 * Normalize sheet rows: detect lat/lng columns, parse to numbers, filter invalid rows.
 * @param {Record<string, unknown>[]} rows
 * @returns {Record<string, unknown>[]}
 */
function normalizeMapRows(rows) {
	if (!Array.isArray(rows) || rows.length === 0) {
		console.log('[googlesheet] normalizeMapRows: no rows or empty array')
		return []
	}
	const keys = Object.keys(rows[0])
	const latKey = findColumn(LAT_NAMES, keys)
	const lngKey = findColumn(LNG_NAMES, keys)
	console.log('[googlesheet] normalizeMapRows: columns', keys, '→ latKey:', latKey, 'lngKey:', lngKey)
	if (!latKey || !lngKey) {
		console.warn('[googlesheet] normalizeMapRows: no lat/lng columns found, returning rows unchanged')
		return rows
	}

	const filtered = rows.filter((row) => hasValidCoords(row, latKey, lngKey))
	console.log('[googlesheet] normalizeMapRows: raw rows', rows.length, '→ with valid coords', filtered.length)
	return filtered.map((row) => {
		const out = { ...row }
		out.latitude = parseCoord(row[latKey])
		out.longitude = parseCoord(row[lngKey])
		return out
	})
}

const setGoogleSheetKey = (key) => {
	state.googleSheetKey = key
}
const setGoogleSheetSheetName = (name) => {
	state.googleSheetSheetName = name
}

function getSheetCaseInsensitive(sheets, requestedName) {
	if (!sheets || typeof sheets !== 'object') return null
	if (requestedName && sheets[requestedName]) return sheets[requestedName]
	const wanted = String(requestedName ?? '').toLowerCase()
	const matchKey = Object.keys(sheets).find((k) => k.toLowerCase() === wanted)
	return matchKey ? sheets[matchKey] : null
}

function looksLikeTemplateSheet(rows) {
	if (!Array.isArray(rows) || rows.length === 0) return false
	const first = rows[0]
	if (!first || typeof first !== 'object') return false
	const keys = Object.keys(first).map((k) => k.toLowerCase())
	return (
		keys.includes('headline') &&
		keys.includes('subtitle') &&
		keys.includes('source') &&
		keys.includes('popuptemplate')
	)
}

function looksLikeLegendSheet(rows) {
	if (!Array.isArray(rows) || rows.length === 0) return false
	const first = rows[0]
	if (!first || typeof first !== 'object') return false
	const keys = Object.keys(first).map((k) => k.toLowerCase())
	return keys.includes('category') && keys.includes('colour')
}

function normalizeLegendRows(rows) {
	if (!Array.isArray(rows)) return []
	return rows
		.map((row) => ({
			category: String(row?.Category ?? '').trim(),
			colour: String(row?.Colour ?? '').trim(),
			label: String(row?.Label ?? row?.Category ?? '').trim(),
		}))
		.filter((row) => row.category && row.colour)
}

function applyCategoryColours(rows, legendRows) {
	if (!Array.isArray(rows) || rows.length === 0) return []
	const legendByCategory = Object.fromEntries(
		(legendRows ?? []).map((item) => [item.category.toLowerCase(), item.colour])
	)
	return rows.map((row) => {
		const category = String(row?.Category ?? '').trim().toLowerCase()
		const pointColor = category ? legendByCategory[category] : null
		return pointColor ? { ...row, __pointColor: pointColor } : row
	})
}

const googleSheetData = async () => {
	const url = `https://interactive.guim.co.uk/docsdata/${state.googleSheetKey}.json`
	//console.log('[googlesheet] googleSheetData: fetching', url, 'sheet:', state.googleSheetSheetName)
	const data = await getJson(url)
	const sheets = data?.sheets ?? {}
	const keys = Object.keys(sheets)
	const sheet =
		getSheetCaseInsensitive(sheets, state.googleSheetSheetName) ?? sheets[keys[0]]
	const template =
		Object.values(sheets).find((rows) => looksLikeTemplateSheet(rows)) ??
		sheets[keys[1]] ??
		[]
	const legendSheet =
		Object.values(sheets).find((rows) => looksLikeLegendSheet(rows)) ?? []
	const raw = Array.isArray(sheet) ? sheet : []
	//console.log('[googlesheet] googleSheetData: raw sheet length', raw.length, 'first row keys:', raw[0] ? Object.keys(raw[0]) : null)
	const legendRows = normalizeLegendRows(legendSheet)
	const normalized = applyCategoryColours(normalizeMapRows(raw), legendRows)
	//console.log('[googlesheet] googleSheetData: normalized length', normalized.length, 'sample:', sample)
	const templateRow = Array.isArray(template) && template[0] ? template[0] : {}
	state.headline = templateRow.headline ?? ''
	state.subtitle = templateRow.subtitle ?? ''
	state.source = templateRow.source ?? ''
	state.popupTemplate = templateRow.popupTemplate ?? ''
	state.legend = legendRows
	state.mapdata = normalized
}

export {
	state,
	googleSheetData,
	setGoogleSheetKey,
	setGoogleSheetSheetName,
}
